import React from "react";
import moment from "moment";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { nestedSurfaceSx, useValueColor } from "./transferTheme";
import { formatGallons, toNumber } from "./transferMath";

const LEVEL_COLOR = { ok: "success", warn: "warning", bad: "error", off: "secondary" };
const LEVEL_ICON = {
  ok: "fa-solid fa-circle-check",
  warn: "fa-solid fa-triangle-exclamation",
  bad: "fa-solid fa-circle-xmark",
  off: "fa-solid fa-circle-minus",
};

function buildChecks({ deviceValue, deviceData, rs485Status, isLive, transferActive }) {
  const audit = deviceData?.audit ?? {};
  const speed = toNumber(deviceValue?.convert_position_speed ?? deviceValue?.location?.speed_mph) ?? 0;
  const moving = audit.motionState === "moving" || deviceValue?.moment_status === "moving" || speed > 0;
  const sensorOk = rs485Status.connectionStatus === "connected" && rs485Status.crcValid !== false;
  const level = toNumber(deviceValue?.level_percent);
  const capacity = toNumber(deviceValue?.capacity_gallons);
  const gallons = toNumber(deviceValue?.volume_gallons);

  return [
    isLive
      ? { level: "ok", title: "Telemetry live", text: `updated ${moment(deviceValue?.last_updated).fromNow()}` }
      : { level: "bad", title: "Telemetry stale", text: deviceValue?.last_updated ? `last ${moment(deviceValue.last_updated).fromNow()}` : "never reported" },
    sensorOk
      ? { level: "ok", title: "Level sensor OK", text: `${rs485Status.port} · CRC ok` }
      : { level: "bad", title: `Sensor ${rs485Status.connectionStatus}`, text: `${rs485Status.consecutiveFailures} consecutive failures` },
    moving && transferActive
      ? { level: "warn", title: "Moving during transfer", text: `${Math.round(speed)} mph` }
      : { level: "ok", title: moving ? "Moving" : "Parked", text: `motion: ${audit.motionState ?? deviceValue?.moment_status ?? "unknown"}` },
    audit.manualCaptureMissing
      ? { level: "bad", title: "No capture for level change", text: `${formatGallons(audit.observedChangeGallons)} observed` }
      : { level: "ok", title: "Changes captured", text: "no unlogged level change" },
    audit.reviewRequired
      ? { level: audit.severity === "high" ? "bad" : "warn", title: "Review required", text: (audit.flags ?? []).join(", ") || audit.classification }
      : { level: "ok", title: "No audit flags", text: `severity: ${audit.severity ?? "none"}` },
    deviceValue?.pressure_mode && deviceValue.pressure_mode !== "neutral"
      ? { level: "ok", title: deviceValue.pressure_mode === "vacuum" ? "Vacuum pulling" : "Pressure pushing", text: `${Math.round(toNumber(deviceValue.pressure_psi) ?? 0)} psi` }
      : { level: "off", title: "Pressure neutral", text: `${Math.round(toNumber(deviceValue?.pressure_psi) ?? 0)} psi` },
    level != null && level >= 90
      ? { level: "bad", title: "Near overfill", text: `${Math.round(level)}% full` }
      : { level: "ok", title: "Headroom OK", text: capacity != null && gallons != null ? `${formatGallons(capacity - gallons)} free` : "--" },
  ];
}

export default function TransferChecks(props) {
  const valueColor = useValueColor();
  const checks = buildChecks(props);

  return (
    <Grid container spacing={1}>
      {checks.map((check) => (
        <Grid item xs={12} sm={6} lg={4} key={check.title}>
          <Paper
            elevation={0}
            sx={(theme) => ({
              ...nestedSurfaceSx(theme),
              p: 1.25,
              height: "100%",
              display: "flex",
              gap: 1.25,
              alignItems: "flex-start",
              borderColor: check.level === "ok" || check.level === "off"
                ? undefined
                : theme.functions.rgba(theme.palette[LEVEL_COLOR[check.level]].main, 0.5),
            })}
          >
            <MDBox color={LEVEL_COLOR[check.level]} lineHeight={1} mt={0.25}>
              <i className={LEVEL_ICON[check.level]} />
            </MDBox>
            <MDBox minWidth={0}>
              <MDTypography variant="button" color={valueColor} fontWeight="bold" display="block">
                {check.title}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {check.text}
              </MDTypography>
            </MDBox>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}
